import clsx from "clsx";
import type { MaMonteurFortschritt, MaPosition, MaStatus } from "@/lib/material";
import {
  formatLieferKwFromFields,
  lieferKwDateBounds,
  type MaLieferKwRange,
} from "@/lib/maLieferKw";

export type MaPositionOrderState =
  | "offen"
  | "entwurf"
  | "bestellt"
  | "geliefert"
  | "bereitgelegt"
  | "storniert"
  | "abgelehnt";

export function maPositionOrderState(p: MaPosition): MaPositionOrderState {
  const closed = String(p.position_status || "").trim().toLowerCase();
  if (closed === "storniert") return "storniert";
  if (closed === "abgelehnt") return "abgelehnt";
  const ordered = p.ordered_links || [];
  if (ordered.length > 0) {
    if (ordered.every((link) => Boolean(link.geliefert_at))) return "geliefert";
    return "bestellt";
  }
  if (p.bereitgelegt_at) return "bereitgelegt";
  if ((p.draft_links?.length ?? 0) > 0) return "entwurf";
  return "offen";
}

export function maPositionOrderLabel(state: MaPositionOrderState): string {
  switch (state) {
    case "geliefert":
      return "Geliefert";
    case "bestellt":
      return "Bestellt";
    case "bereitgelegt":
      return "Bereitgelegt";
    case "storniert":
      return "Storniert";
    case "abgelehnt":
      return "Abgelehnt";
    case "entwurf":
      return "Im Entwurf";
    default:
      return "Offen";
  }
}

const MA_POSITION_ORDER_PILL: Record<MaPositionOrderState, string> = {
  offen: "bg-status-orange/15 text-status-orange",
  entwurf: "bg-accent-soft text-accent",
  bestellt: "bg-indigo/15 text-indigo",
  geliefert: "bg-status-green/15 text-status-green",
  bereitgelegt: "bg-status-green/15 text-status-green",
  storniert: "bg-fill-tertiary text-label-secondary",
  abgelehnt: "bg-status-red/15 text-status-red",
};

export function maPositionOrderHint(p: MaPosition): string | null {
  const state = maPositionOrderState(p);
  if (state === "storniert" || state === "abgelehnt") {
    const grund = (p.position_status_grund || "").trim();
    return grund || null;
  }
  const parts: string[] = [];
  for (const link of p.ordered_links || []) {
    const tk =
      (link.bestellnummer || "").trim() ||
      (link.order_id != null ? `TK #${link.order_id}` : "TK");
    const supplier = (link.supplier_name || "").trim();
    parts.push(supplier ? `${tk} · ${supplier}` : tk);
  }
  for (const link of p.draft_links || []) {
    const supplier = (link.supplier_name || "").trim();
    parts.push(
      supplier ? `Entwurf · ${supplier}` : `Entwurf #${link.draft_id}`,
    );
  }
  return parts.length ? parts.join(" · ") : null;
}

export function maPositionOrderSummary(positionen: MaPosition[] | null | undefined): {
  total: number;
  ordered: number;
  geliefert: number;
  bereitgelegt: number;
  draftOnly: number;
  open: number;
  storniert: number;
  abgelehnt: number;
} {
  const list = positionen || [];
  let ordered = 0;
  let geliefert = 0;
  let bereitgelegt = 0;
  let draftOnly = 0;
  let open = 0;
  let storniert = 0;
  let abgelehnt = 0;
  for (const p of list) {
    const state = maPositionOrderState(p);
    if (state === "geliefert") geliefert += 1;
    else if (state === "bestellt") ordered += 1;
    else if (state === "bereitgelegt") bereitgelegt += 1;
    else if (state === "storniert") storniert += 1;
    else if (state === "abgelehnt") abgelehnt += 1;
    else if (state === "entwurf") draftOnly += 1;
    else open += 1;
  }
  return {
    total: list.length,
    ordered,
    geliefert,
    bereitgelegt,
    draftOnly,
    open,
    storniert,
    abgelehnt,
  };
}

/** z. B. „4× Geliefert und 1× Bereitgelegt“ */
export function formatMaPositionOrderSummary(
  positionen: MaPosition[] | null | undefined,
): string {
  const s = maPositionOrderSummary(positionen);
  const bits: string[] = [];
  if (s.geliefert) bits.push(`${s.geliefert}× Geliefert`);
  if (s.ordered) bits.push(`${s.ordered}× Bestellt`);
  if (s.bereitgelegt) bits.push(`${s.bereitgelegt}× Bereitgelegt`);
  if (s.draftOnly) bits.push(`${s.draftOnly}× Entwurf`);
  if (s.storniert) bits.push(`${s.storniert}× Storniert`);
  if (s.abgelehnt) bits.push(`${s.abgelehnt}× Abgelehnt`);
  if (s.open) bits.push(`${s.open}× Offen`);
  if (bits.length === 2) return bits.join(" und ");
  return bits.join(" · ");
}

export function MaPositionOrderBadge({ position }: { position: MaPosition }) {
  const state = maPositionOrderState(position);
  const hint = maPositionOrderHint(position);
  return (
    <span className="inline-flex max-w-full flex-col items-start gap-0.5">
      <span
        className={clsx(
          "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
          MA_POSITION_ORDER_PILL[state],
        )}
        title={hint || undefined}
      >
        {maPositionOrderLabel(state)}
      </span>
      {hint ? (
        <span className="text-[11px] leading-snug text-label-tertiary">{hint}</span>
      ) : null}
    </span>
  );
}

export function fmtMaDateOnly(value: string | null | undefined): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function fmtMaDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Alter seit einem Zeitpunkt.
 *  Bis 5 Minuten: „gerade eben“.
 *  Darüber Minuten, ab einer Stunde Stunden und Minuten.
 *  Ab 24 Stunden nur noch Tage und Stunden.
 */
export function formatRequestAge(value: string | null | undefined): string {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const diffMs = Date.now() - then.getTime();
  if (diffMs <= 5 * 60 * 1000) return "gerade eben";

  const totalMinutes = Math.floor(diffMs / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    const dayLabel = days === 1 ? "1 Tag" : `${days} Tage`;
    return hours > 0 ? `${dayLabel} ${hours} Std.` : dayLabel;
  }
  if (hours === 0) return `${minutes} Min.`;
  if (minutes === 0) return `${hours} Std.`;
  return `${hours} Std. ${minutes} Min.`;
}

export function localTodayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export type LieferterminTone = "none" | "normal" | "soon" | "today" | "past";

/** Visuelle Dringlichkeit für offene Anforderungen mit Liefertermin. */
export function lieferterminTone(
  liefertermin: string | null | undefined,
  status: MaStatus,
  kwEndIso?: string | null,
): LieferterminTone {
  const effective = liefertermin || kwEndIso || null;
  if (!effective) return "none";
  if (status === "erledigt" || status === "abgelehnt") return "normal";
  const today = localTodayIso();
  if (effective < today) return "past";
  if (effective === today) return "today";
  const inThreeDays = new Date();
  inThreeDays.setDate(inThreeDays.getDate() + 3);
  const cap = `${inThreeDays.getFullYear()}-${String(inThreeDays.getMonth() + 1).padStart(2, "0")}-${String(inThreeDays.getDate()).padStart(2, "0")}`;
  if (effective <= cap) return "soon";
  return "normal";
}

const EARLY_FORTSCHRITT = new Set(["bestellt", "bereitgelegt", "geliefert"]);

/**
 * Unbearbeitete MA mit verbindlichem Fixtermin ≤ 3 Tage (inkl. überfällig).
 * Entspricht dem Nav-Badge `material-open-urgent-fix`.
 */
export function maIsUrgentUnclaimedFix(ma: {
  status: MaStatus;
  bearbeitet_von?: string | null;
  fortschritt?: string | null;
  liefertermin?: string | null;
  liefertermin_fix?: boolean;
}): boolean {
  if (ma.status !== "offen") return false;
  if ((ma.bearbeitet_von || "").trim()) return false;
  if (EARLY_FORTSCHRITT.has(String(ma.fortschritt || "").trim())) return false;
  if (!ma.liefertermin_fix || !ma.liefertermin) return false;
  const tone = lieferterminTone(ma.liefertermin, ma.status);
  return tone === "past" || tone === "today" || tone === "soon";
}

export function maLieferKwLabel(ma: {
  liefer_kw_von?: number | null;
  liefer_kw_bis?: number | null;
  liefer_kw_jahr?: number | null;
}): string {
  return formatLieferKwFromFields(
    ma.liefer_kw_von,
    ma.liefer_kw_bis,
    ma.liefer_kw_jahr,
  );
}

export function maLieferKwEndIso(ma: {
  liefer_kw_von?: number | null;
  liefer_kw_bis?: number | null;
  liefer_kw_jahr?: number | null;
}): string | null {
  if (
    ma.liefer_kw_von == null ||
    ma.liefer_kw_bis == null ||
    ma.liefer_kw_jahr == null
  ) {
    return null;
  }
  const range: MaLieferKwRange = {
    von: ma.liefer_kw_von,
    bis: ma.liefer_kw_bis,
    jahr: ma.liefer_kw_jahr,
  };
  return lieferKwDateBounds(range).maxIso;
}

export function maLieferterminSummary(ma: {
  liefertermin?: string | null;
  liefertermin_fix?: boolean;
  liefer_kw_von?: number | null;
  liefer_kw_bis?: number | null;
  liefer_kw_jahr?: number | null;
}): string {
  const kw = maLieferKwLabel(ma);
  const date = ma.liefertermin ? fmtMaDateOnly(ma.liefertermin) : "";
  if (kw && date) {
    return ma.liefertermin_fix ? `${kw} · Fix ${date}` : `${kw} · ${date}`;
  }
  if (kw) return kw;
  if (date) return ma.liefertermin_fix ? `Fix ${date}` : date;
  return "";
}

export function maAuftragLabel(ma: MaProjectLineSource): string {
  if (ma.auftrag_nummer) return ma.auftrag_nummer;
  if (ma.title) return ma.title;
  return "Materialanforderung";
}

export function maIsOpen(status?: MaStatus | null): boolean {
  return status !== "erledigt" && status !== "abgelehnt";
}

export function maFortschrittLabel(f: MaMonteurFortschritt): string {
  switch (f) {
    case "offen":
      return "Offen";
    case "in_bearbeitung":
      return "In Bearbeitung";
    case "angefragt":
      return "Angefragt";
    case "bestellt":
      return "Bestellt";
    case "bereitgelegt":
      return "Im Lager bereitgelegt";
    case "geliefert":
      return "Geliefert";
    case "abgelehnt":
      return "Storniert";
    default:
      return f;
  }
}

export const MA_FORTSCHRITT_PILL: Record<MaMonteurFortschritt, string> = {
  offen: "bg-status-orange/15 text-status-orange",
  in_bearbeitung: "bg-accent-soft text-accent",
  angefragt: "bg-status-orange/15 text-status-orange",
  bestellt: "bg-indigo/15 text-indigo",
  bereitgelegt:
    "bg-[color-mix(in_srgb,_var(--eh-green)_20%,_var(--eh-bg-card))] text-status-green",
  geliefert:
    "bg-[color-mix(in_srgb,_var(--eh-green)_20%,_var(--eh-bg-card))] text-status-green",
  abgelehnt: "bg-fill-tertiary text-label-secondary line-through",
};

const KNOWN_FORTSCHRITT = new Set<string>(Object.keys(MA_FORTSCHRITT_PILL));

/** Monteur-Stand immer als bekanntes Badge-Wert normalisieren. */
export function normalizeMonteurFortschritt(
  f: MaMonteurFortschritt | string | null | undefined,
  status?: MaStatus | null,
): MaMonteurFortschritt {
  if (f && KNOWN_FORTSCHRITT.has(f)) return f as MaMonteurFortschritt;
  if (status === "abgelehnt") return "abgelehnt";
  if (status === "erledigt") return "geliefert";
  if (status === "in_bearbeitung") return "in_bearbeitung";
  return "offen";
}

export function maBearbeiterDisplayName(
  ma: {
    bearbeitet_von_name?: string | null;
    bearbeitet_von?: string | null;
  },
): string | null {
  const name = (ma.bearbeitet_von_name || ma.bearbeitet_von || "").trim();
  return name || null;
}

export function MaFortschrittBadge({
  f,
  status,
}: {
  f?: MaMonteurFortschritt | string | null;
  status?: MaStatus | null;
}) {
  const normalized = normalizeMonteurFortschritt(f, status);
  if (!maIsOpen(status)) {
    return (
      <span className="text-xs text-label-secondary">{maFortschrittLabel(normalized)}</span>
    );
  }
  return (
    <span
      className={clsx(
        "inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold",
        MA_FORTSCHRITT_PILL[normalized],
      )}
    >
      {maFortschrittLabel(normalized)}
    </span>
  );
}

export function maAuftragSubline(ma: MaProjectLineSource): string | null {
  if (ma.title && ma.auftrag_nummer) return ma.title;
  if (ma.notes) {
    const n = ma.notes.trim();
    if (n.length > 80) return `${n.slice(0, 77)}…`;
    return n;
  }
  return null;
}

/** Projektzeile wie in der Bestellungsliste (Auftrag · Ort · Beschreibung). */
export type MaProjectLineSource = {
  auftrag_nummer?: string | null;
  auftrag_ort?: string | null;
  auftrag_beschreibung?: string | null;
  title?: string | null;
  notes?: string | null;
};

export function maProjectLine(ma: MaProjectLineSource): string | null {
  const parts = [
    ma.auftrag_nummer,
    ma.auftrag_ort,
    ma.auftrag_beschreibung,
    ma.title && !ma.auftrag_nummer ? ma.title : null,
  ]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean);
  if (!parts.length) return maAuftragSubline(ma);
  return parts.join(" · ");
}
